import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportError } from './crash.ts';

/**
 * The page that shows instead of a blank one — with error reporting
 * (crash.ts), which is off until the site's Worker has a SENTRY_DSN.
 *
 * Without this, one component that throws while rendering unmounts the whole
 * tree and leaves a white page with nothing to press. A nurse mid-round, or
 * a daughter checking whether her mother took her tablets, gets no hint
 * that reloading would fix it.
 *
 * English only, on purpose: whatever crashed may be the translations, and
 * this screen must not depend on them. Styled with inline styles and the
 * site's own colour tokens (with fallbacks), so it renders even if the
 * stylesheet is what failed.
 *
 * `#crash-test` on any page throws inside the boundary, so the owner can see
 * this screen and the report arrive in Sentry (docs/07-deployment.md,
 * "Error reporting"). It affects only the person who typed it.
 */
interface Props {
  children: ReactNode;
}

interface State {
  failed: boolean;
}

/** Throws on render when the address ends in #crash-test. */
function CrashTest(): null {
  if (typeof window !== 'undefined' && window.location.hash === '#crash-test') {
    throw new TypeError('DrBell crash test (#crash-test)');
  }
  return null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    reportError(error, 'boundary', info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.failed) {
      return (
        <>
          <CrashTest />
          {this.props.children}
        </>
      );
    }
    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          background: 'var(--bg, transparent)',
          color: 'var(--fg, inherit)',
          fontFamily: 'inherit',
        }}
      >
        <div
          style={{
            maxWidth: '420px',
            width: '100%',
            padding: '24px',
            borderRadius: '16px',
            border: '1px solid var(--line, var(--rule, rgba(127,127,127,.35)))',
            background: 'var(--panel, transparent)',
            textAlign: 'center',
          }}
        >
          <h1 style={{ fontSize: '20px', fontWeight: 600, margin: '0 0 8px' }}>Something went wrong</h1>
          <p style={{ margin: '0 0 20px', color: 'var(--muted, inherit)', lineHeight: 1.5 }}>
            This page hit a problem. Reloading usually fixes it. Anything already saved is safe.
          </p>
          <button
            type="button"
            onClick={() => {
              // Drop #crash-test, or the reload would crash again.
              if (window.location.hash === '#crash-test') {
                window.history.replaceState(null, '', window.location.pathname + window.location.search);
              }
              window.location.reload();
            }}
            style={{
              minHeight: '44px',
              padding: '0 20px',
              borderRadius: '12px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 600,
              color: '#fff',
              background: 'var(--accent, var(--color-accent, #0f766e))',
            }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
